/*
 * Copyright (c) 2024. Bolmate B.V.
 */

import React, {
    ForwardedRef, forwardRef, MutableRefObject, useContext, useImperativeHandle, useMemo, useRef, useState
} from 'react';
import {AppContext} from "../../misc/appContext";
import BolProduct from "../../../models/bolProductModel";
import {ActivateHandle} from "../../../util/interfaces";
import {ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import Account, {ImportingStat} from "../../../models/accountModel";
import {createErrorMessage} from "../../../util/utils";
import ProductSelect from "./productSelect";

interface Props {
    onSubmit: (exceptEANS: Array<string>) => void;
    loading: boolean;
}

type ActivateConfig = {
    selectedEANS: Array<string> | Set<string> | null;
}

export type ProductSelectHandle = ActivateHandle<ActivateConfig> & {close: VoidFunction};

type LoadConfig = Partial<ActivateConfig> & {page?: number; search?: string; exceptProducts?: Array<BolProduct>;};

function getRemainingProducts(products: Array<BolProduct>,
                              exceptProducts: Array<BolProduct> | undefined): Array<BolProduct> {
    if (!exceptProducts) return products;
    const remaining: Array<BolProduct> = [];
    for (const product of products) {
        if (!exceptProducts.find((prod: BolProduct) => prod.ean === product.ean))
            remaining.push(product);
    }
    return remaining;
}

function listContainsProduct(list: Array<BolProduct>, product: BolProduct): boolean {
    return !!list.find((prod: BolProduct) => prod.ean === product.ean);
}

const SEARCH_TIMEOUT = 300;
const LIMIT = 100;
const SYNC_TIMEOUT = 5000;

function ProductSelectController(props: Props, ref: ForwardedRef<ProductSelectHandle>) {
    const {account} = useContext(AppContext).accountContext;
    const {onSubmit} = props;
    const [allSelected, setAllSelected] = useState(false);
    const [remainingSelected, setRemainingSelected] = useState(false);
    const [products, setProducts] = useState<Array<BolProduct>>([]);
    const [except, setExcept] = useState<Array<BolProduct>>([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [search, setSearch] = useState('');
    const [active, setActive] = useState(false);
    const [totalProducts, setTotalProducts] = useState(0);
    const [importing, setImporting] = useState(false);
    const searchTimeout = useRef<number | undefined>(undefined);
    const syncTimeout = useRef<number | undefined>(undefined);
    const hasChanged = useRef<boolean>(false);
    const filteredExcept: Array<BolProduct> = useMemo(() => {
        if (!search.length) return except;
        const lowerSearch = search.toLowerCase();
        return except.filter((product: BolProduct) => {
            return product.ean.toLowerCase().includes(lowerSearch) || product.title.toLowerCase().includes(lowerSearch);
        });
    }, [search, except]);

    function onActivate(config: ActivateConfig) {
        const eans = !config.selectedEANS ? [] : Array.from(config.selectedEANS);
        setAllSelected(eans.includes('all'));
        setRemainingSelected(eans.includes('remaining'));
        window.setTimeout(() => {
            setActive(true);
            loadExcept(config);
            checkSync();
        });
    }

    function onClose() {
        setActive(false);
        setAllSelected(false);
        setRemainingSelected(false);
        setProducts([]);
        setExcept([]);
        setLoading(false);
        setLoadingMore(false);
        setSearch('');
        setTotalProducts(0);
        setImporting(false);
        clearTimeouts();
    }

    function onChangeSearch(value: string) {
        abortTimeout(searchTimeout);
        setSearch(value);
        searchTimeout.current = window.setTimeout(() => {
            loadProducts({search: value});
        }, SEARCH_TIMEOUT);
    }

    function onEnterSearch() {
        abortTimeout(searchTimeout);
        loadExcept();
    }

    function onSelectAll() {
        setAllSelected(!allSelected);
        setExcept([]);
        setRemainingSelected(false);
        hasChanged.current = true;
    }

    function onSelectRemaining() {
        setExcept([]);
        setRemainingSelected(!remainingSelected);
        setAllSelected(false);
        hasChanged.current = true;
    }

    function onClickSync() {
        if (!account) return;
        BolProduct.importAsync(account.id, false, (_: Account, success: boolean, data: ResponseData) => {
            if (success) {
                showSnackbar('De producten worden geïmporteerd');
                checkSync();
            } else
                showDialog(createErrorMessage(data.message ?? 'Het importeren van je producten is niet gelukt.'),
                    'Importeren mislukt');
        });
    }

    function onSelectProduct(product: BolProduct) {
        return function() {
            let _except = except.slice();
            if (listContainsProduct(_except, product))
                _except = _except.filter((prod: BolProduct) => prod.ean !== product.ean);
            else
                _except.push(product);
            setRemainingSelected(false);
            setExcept(_except);
            hasChanged.current = true;
        }
    }

    function _onSubmit() {
        if (search.length) {
            onChangeSearch('');
            loadProducts({search: ''});
            return;
        }
        let selected: Array<string> = [];
        if (remainingSelected) selected = ['remaining'];
        else {
            if (allSelected) selected = ['all'];
            selected = selected.concat(except.map((product: BolProduct) => product.ean));
        }
        onSubmit(selected);
    }

    function onLoadMore(startIndex: number): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (!account) return reject();
            setLoadingMore(true);
            const page = Math.floor(startIndex / LIMIT);
            BolProduct.getAll({accountId: account.id, limit: LIMIT, page, search},
                (_products: Array<BolProduct>, success: boolean) => {
                    setLoadingMore(false);
                    if (!success) return reject();
                    setProducts(products.concat(_products));
                    resolve();
                });
        });
    }

    function loadExcept(config?: LoadConfig) {
        if (!account) return;
        setLoading(true);
        const _search = config?.search ?? search;
        const eans = !config?.selectedEANS ? [] : Array.from(config.selectedEANS);
        let _except = eans ?? (config?.exceptProducts ?? except)?.map(
            (product: BolProduct) => product.ean);
        _except = _except.filter((value: string) => !['all', 'remaining'].includes(value));
        if (!_except?.length) return loadProducts(config);
        BolProduct.getAll({accountId: account.id, limit: -1, page: -1, search: _search, eans: _except},
            (products: Array<BolProduct>, success: boolean) => {
                if (success) setExcept(products);
                loadProducts({...config, exceptProducts: products})
            });
    }

    function loadProducts(config?: LoadConfig) {
        const _search = config?.search ?? search;
        const _page = config?.page ?? 0;
        BolProduct.getAll({accountId: account!.id, limit: LIMIT, page: _page, search: _search},
            (products: Array<BolProduct>, success: boolean, data: ResponseData) => {
                if (!success) {
                    setLoading(false);
                    return;
                }
                const remainingProducts = getRemainingProducts(products, config?.exceptProducts);
                if (products.length && !remainingProducts.length) return loadProducts({...config, page: _page + 1});
                setProducts(products);
                setTotalProducts(data.total);
                setLoading(false);
            });
    }

    function checkSync() {
        abortTimeout(syncTimeout);
        if (!account) return;
        account.getImportingProductsCount().then((data: ImportingStat) => {
            const {importing} = data;
            setImporting(importing);
            if (importing) syncTimeout.current = window.setTimeout(checkSync, SYNC_TIMEOUT);
        }).catch((error?: string) => {
            setImporting(false);
            if (error) showDialog(createErrorMessage(error), 'Importeren mislukt');
        });
    }

    function abortTimeout(ref: MutableRefObject<number | undefined>) {
        window.clearTimeout(ref.current);
        ref.current = undefined;
    }

    function clearTimeouts() {
        abortTimeout(searchTimeout);
        abortTimeout(syncTimeout);
    }

    useImperativeHandle(ref, () => ({activate: onActivate, close: onClose}));

    return (
        <ProductSelect onClose={onClose} onSelectAll={onSelectAll} onSelectRemaining={onSelectRemaining}
                       onSelectProduct={onSelectProduct} onLoadMore={onLoadMore} onChangeSearch={onChangeSearch}
                       onImportProducts={onClickSync} onSubmit={_onSubmit} onEnterSearch={onEnterSearch}
                       products={products} except={filteredExcept} active={active} loading={loading || props.loading}
                       search={search} totalProducts={totalProducts} importing={importing} allSelected={allSelected}
                       remainingSelected={remainingSelected} loadingMore={loadingMore}/>
    )
}

export default forwardRef<ProductSelectHandle, Props>(ProductSelectController);